import { useState } from 'react'
import { formatRelative } from '../../lib/dates'

/**
 * How much to trust the number next to it.
 *
 * Confidence is a promise about evidence, so the explanation states the
 * evidence rather than a word like "medium": how many observations, and how
 * long ago the last one was. A parent can then decide for themselves, which is
 * the only honest way to show a number a machine inferred.
 *
 * It is a button, not a hover tooltip — this is a phone-first product and a
 * tooltip nobody can reach is worse than no tooltip. Inside something that is
 * already a button (a domain tile), pass `interactive={false}`: the dot still
 * shows and the sentence goes in its title, but nothing nests.
 */
export function ConfidenceDot({
  confidence,
  observationCount,
  lastObservedAt,
  size = 'md',
  interactive = true,
}: {
  confidence?: number
  observationCount?: number
  lastObservedAt?: number
  size?: 'sm' | 'md'
  interactive?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (typeof confidence !== 'number') return null

  const sentence = explainConfidence(confidence, observationCount, lastObservedAt)

  if (!interactive) {
    return (
      <span className={`conf conf-btn conf-${size}`} title={`How sure we are: ${sentence}`}>
        <span className="conf-dot" aria-hidden="true">
          <span className="conf-dot-fill" style={{ transform: `scale(${scaleFor(confidence)})` }} />
        </span>
      </span>
    )
  }

  return (
    <span className="conf">
      <button
        type="button"
        className={`conf-btn conf-${size}`}
        aria-expanded={open}
        aria-label={`How sure we are: ${sentence}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="conf-dot" aria-hidden="true">
          <span className="conf-dot-fill" style={{ transform: `scale(${scaleFor(confidence)})` }} />
        </span>
      </button>
      {open && (
        <span className="conf-pop" role="note">
          {sentence}
        </span>
      )}
    </span>
  )
}

/** A dot that grows with confidence — never smaller than clearly visible. */
function scaleFor(confidence: number): number {
  return 0.35 + Math.max(0, Math.min(1, confidence)) * 0.65
}

export function confidenceWord(confidence: number): string {
  if (confidence >= 0.8) return 'a well established read'
  if (confidence >= 0.6) return 'a reasonably sure read'
  if (confidence >= 0.4) return 'an early read'
  return 'a first impression'
}

/**
 * The honest sentence. It never says "low confidence" as a verdict — it says
 * what we have, and what would sharpen it.
 */
export function explainConfidence(
  confidence: number,
  observationCount?: number,
  lastObservedAt?: number,
): string {
  const parts: string[] = []

  if (typeof observationCount === 'number' && observationCount > 0) {
    parts.push(
      `Based on ${observationCount} observation${observationCount === 1 ? '' : 's'}`,
    )
    if (lastObservedAt) parts.push(`most recent ${formatRelative(lastObservedAt)}`)
  } else if (lastObservedAt) {
    parts.push(`Last seen ${formatRelative(lastObservedAt)}`)
  }

  const head = parts.length ? `${parts.join(', ')}. ` : ''
  const tail =
    confidence < 0.6
      ? `This is ${confidenceWord(confidence)}. One more moment noticed would sharpen it.`
      : `This is ${confidenceWord(confidence)}.`

  return `${head}${tail}`
}
